// Formatted .xlsx export for the Cashflow projection page.
//
// Produces a single worksheet that mirrors the on-screen grid:
//   header row of months (with ACTUAL/FORECAST tags)
//   opening operating
//   income — section headers, line rows, total
//   expenses — section headers, line rows, total
//   GST, TD maturity, drawdowns, closing operating, total liquid
//
// Uses the same CCAANZ palette + ACCOUNTING number format as the budget
// xlsx export. Frozen top header row + first two columns.

import type { Workbook, Row, Cell, Worksheet } from 'exceljs';
import type { BudgetData } from '../types';
import type { CashflowProjection, XeroCashByMonthData, XeroCashByMonthRow } from '../cashflowTypes';

const BRAND = 'FF85754D';
const BLUE = 'FF005487';
const BLUE_LIGHT = 'FFE5EEF4';
const TEXT = 'FF111827';
const TEXT_SOFT = 'FF6B7280';
const BORDER = 'FFE5E0D5';
const ACTUAL_TINT = 'FFF8F4EA'; // opaque equivalent of 6% gold over white

const ACCOUNTING = '#,##0;(#,##0);"-";@';

const round = (n: number) => Math.round(n);

function monthLabel(ym: string): string {
  const parts = ym.split('-').map((s) => parseInt(s, 10));
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
}

type RowKind =
  'header' | 'section-header' | 'category' | 'data' | 'subtotal' | 'total' | 'closing' | 'liquid';

function styleRow(row: Row, kind: RowKind, monthCount: number): void {
  const monthCells: Cell[] = [];
  for (let i = 3; i <= 2 + monthCount; i += 1) monthCells.push(row.getCell(i));

  switch (kind) {
    case 'header':
      row.font = { bold: true, color: { argb: BRAND }, size: 11 };
      row.height = 24;
      row.eachCell((cell) => {
        cell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
        cell.border = { bottom: { style: 'medium', color: { argb: BORDER } } };
      });
      // Left two header cells aligned left.
      row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
      break;
    case 'section-header':
      row.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      row.height = 22;
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE } };
      });
      break;
    case 'category':
      row.font = { bold: true, color: { argb: BLUE }, size: 11 };
      row.height = 18;
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_LIGHT } };
      });
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
    case 'data':
      row.font = { color: { argb: TEXT }, size: 10 };
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
    case 'subtotal':
      row.font = { italic: true, color: { argb: TEXT_SOFT }, size: 10 };
      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin', color: { argb: BORDER } } };
      });
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
    case 'total':
      row.font = { bold: true, color: { argb: TEXT }, size: 11 };
      row.height = 22;
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'medium', color: { argb: TEXT } },
          bottom: { style: 'thin', color: { argb: TEXT } },
        };
      });
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
    case 'closing':
      row.font = { bold: true, color: { argb: BLUE }, size: 12 };
      row.height = 24;
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_LIGHT } };
        cell.border = {
          top: { style: 'medium', color: { argb: BLUE } },
          bottom: { style: 'medium', color: { argb: BLUE } },
        };
      });
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
    case 'liquid':
      row.font = { color: { argb: TEXT_SOFT }, size: 10 };
      monthCells.forEach((cell) => {
        cell.numFmt = ACCOUNTING;
        cell.alignment = { horizontal: 'right' };
      });
      break;
  }
}

/** Tint actual-month cells in a data/subtotal/total row with the same
 *  cream-gold the screen uses, so the boundary between actual and
 *  forecast is visible at a glance. */
function tintActualCells(row: Row, months: CashflowProjection['months']): void {
  months.forEach((m, i) => {
    if (m.status !== 'actual') return;
    const cell = row.getCell(3 + i);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: ACTUAL_TINT },
    };
  });
}

export type CashflowXlsxInput = {
  projection: CashflowProjection;
  sheetData: XeroCashByMonthData | null;
  budget: BudgetData;
};

/** Add a fully-styled "Cashflow" worksheet to an existing workbook. Used
 *  both by the standalone cashflow xlsx export and by the combined
 *  budget+cashflow workbook. */
export function addCashflowWorksheet(
  wb: Workbook,
  input: CashflowXlsxInput,
  sheetName: string = 'Cashflow',
): Worksheet {
  const { projection, sheetData, budget } = input;
  const months = projection.months;
  const monthCount = months.length;

  // Code → budget line id, used to pull values out of incomeByLine /
  // expenseByLine. Same fallback pattern as the on-screen render.
  const codeToIncomeId = new Map<string, string>();
  const codeToExpenseId = new Map<string, string>();
  for (const line of budget.income) {
    if (line.code) codeToIncomeId.set(line.code, line.id);
  }
  for (const line of budget.expenses) {
    if (line.code) codeToExpenseId.set(line.code, line.id);
  }

  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 5 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  });

  // Column widths.
  ws.getColumn(1).width = 8; // Code
  ws.getColumn(2).width = 38; // Line
  for (let i = 0; i < monthCount; i += 1) {
    ws.getColumn(3 + i).width = 12;
  }

  // ── Title rows ──────────────────────────────────────────────
  const title = ws.addRow(['CCAANZ Diocesan Cashflow Projection']);
  title.font = { bold: true, color: { argb: BRAND }, size: 14 };
  ws.mergeCells(1, 1, 1, 2 + monthCount);

  const meta = ws.addRow([
    `Generated ${new Date().toLocaleString()} · ${monthCount} months · base year ${budget.settings.baseYear}`,
  ]);
  meta.font = { color: { argb: TEXT_SOFT }, italic: true, size: 10 };
  ws.mergeCells(2, 1, 2, 2 + monthCount);

  ws.addRow([]); // spacer

  // ── Status row (ACTUAL / FORECAST) ─────────────────────────
  const statusRow = ws.addRow([
    '',
    '',
    ...months.map((m) => (m.status === 'actual' ? 'ACTUAL' : 'FORECAST')),
  ]);
  statusRow.font = { color: { argb: TEXT_SOFT }, size: 9, italic: true };
  statusRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'right' };
  });
  tintActualCells(statusRow, months);

  // ── Header row (month labels) ──────────────────────────────
  const headerRow = ws.addRow(['Code', 'Line', ...months.map((m) => monthLabel(m.ym))]);
  styleRow(headerRow, 'header', monthCount);
  tintActualCells(headerRow, months);

  // ── Opening operating ──────────────────────────────────────
  const openingRow = ws.addRow([
    '',
    'Opening operating',
    ...months.map((m) => round(m.openingOperating)),
  ]);
  styleRow(openingRow, 'subtotal', monthCount);
  tintActualCells(openingRow, months);

  ws.addRow([]); // spacer

  // ── Section grouping (by Xero section name from sheet data) ──
  // Mirrors what's on screen: rows are grouped by sheet section.
  // Income vs expense classification: trust budget link first, else
  // fall back to the row's own isIncome flag.
  type Group = { section: string; rows: XeroCashByMonthRow[] };
  const incomeGroups: Group[] = [];
  const expenseGroups: Group[] = [];

  if (sheetData) {
    const seen = new Map<string, Group[]>();
    for (const row of sheetData.rows) {
      const inBudgetIncome = codeToIncomeId.has(row.code);
      const inBudgetExpense = codeToExpenseId.has(row.code);
      const isIncome = inBudgetIncome ? true : inBudgetExpense ? false : row.isIncome;
      const target = isIncome ? incomeGroups : expenseGroups;
      const key = (isIncome ? 'i:' : 'e:') + row.section;
      const existing = seen.get(key);
      if (existing && existing[0]?.section === row.section) {
        existing.at(-1)?.rows.push(row);
      } else {
        const g: Group = { section: row.section, rows: [row] };
        target.push(g);
        seen.set(key, [g]);
      }
    }
  }

  function emitGroup(
    groups: Group[],
    direction: 'income' | 'expense',
    sectionLabel: string,
  ): number {
    // Section header row
    const sec = ws.addRow(['', sectionLabel.toUpperCase(), ...new Array(monthCount).fill('')]);
    styleRow(sec, 'section-header', monthCount);

    let grandTotal = 0;
    const grandPerMonth = new Array(monthCount).fill(0);

    if (groups.length === 0) {
      const empty = ws.addRow(['', '(no data)']);
      empty.font = { italic: true, color: { argb: TEXT_SOFT }, size: 10 };
    }

    for (const g of groups) {
      // Group/category sub-header
      if (g.section) {
        const cat = ws.addRow(['', g.section, ...new Array(monthCount).fill('')]);
        styleRow(cat, 'category', monthCount);
      }
      const subPerMonth = new Array(monthCount).fill(0);
      for (const row of g.rows) {
        const lineId =
          direction === 'income'
            ? (codeToIncomeId.get(row.code) ?? `_xero_${row.code}`)
            : (codeToExpenseId.get(row.code) ?? `_xero_${row.code}`);
        const values = months.map((m) => {
          const byLine = direction === 'income' ? m.incomeByLine : m.expenseByLine;
          const v = byLine[lineId] ?? 0;
          return round(v);
        });
        const lineRow = ws.addRow([row.code, row.name, ...values]);
        styleRow(lineRow, 'data', monthCount);
        tintActualCells(lineRow, months);
        values.forEach((v, i) => {
          subPerMonth[i] += v;
        });
      }
      // Group subtotal
      const subRow = ws.addRow(['', `${g.section || direction} subtotal`, ...subPerMonth]);
      styleRow(subRow, 'subtotal', monthCount);
      tintActualCells(subRow, months);
      subPerMonth.forEach((v, i) => {
        grandPerMonth[i] += v;
      });
      grandTotal += subPerMonth.reduce((s, v) => s + v, 0);
    }

    // Direction total
    const totalRow = ws.addRow(['', `Total ${direction}`, ...grandPerMonth]);
    styleRow(totalRow, 'total', monthCount);
    tintActualCells(totalRow, months);
    return grandTotal;
  }

  emitGroup(incomeGroups, 'income', 'Income');
  ws.addRow([]);
  emitGroup(expenseGroups, 'expense', 'Expenses');
  ws.addRow([]);

  // ── GST / TD / Drawdowns / Closing ─────────────────────────
  const gstRow = ws.addRow(['', 'GST Refund (Payment)', ...months.map((m) => round(-m.gstNet))]);
  styleRow(gstRow, 'data', monthCount);
  tintActualCells(gstRow, months);

  const tdRow = ws.addRow(['', 'TD maturity in', ...months.map((m) => round(m.tdMaturityIn))]);
  styleRow(tdRow, 'data', monthCount);
  tintActualCells(tdRow, months);

  const drawRow = ws.addRow([
    '',
    'Waterfall drawdowns',
    ...months.map((m) => round(m.drawdowns.reduce((s, d) => s + d.amount, 0))),
  ]);
  styleRow(drawRow, 'data', monthCount);
  tintActualCells(drawRow, months);

  const closingRow = ws.addRow([
    '',
    'Closing operating',
    ...months.map((m) => round(m.closingOperating)),
  ]);
  styleRow(closingRow, 'closing', monthCount);
  tintActualCells(closingRow, months);

  const liquidRow = ws.addRow([
    '',
    'Total liquid (incl. savings + TD principals)',
    ...months.map((m) => round(m.closingTotalLiquid)),
  ]);
  styleRow(liquidRow, 'liquid', monthCount);
  tintActualCells(liquidRow, months);

  return ws;
}
